import { useState, useEffect } from 'react';
import { ENDPOINTS, resolveAssetUrl, resolveHtmlAssetUrls } from '../config';

export default function KcbOffers() {
    const [scrolled, setScrolled] = useState(false);
    const [policy, setPolicy] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const handleScroll = () => { setScrolled(window.scrollY > 50); };
        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    useEffect(() => {
        const fetchPolicy = async () => {
            try {
                const res = await fetch(ENDPOINTS.POLICIES.BY_SLUG('kcb-offers'));
                const data = await res.json();
                if (data.isOk && data.data) {
                    setPolicy(data.data);
                } else {
                    // Try alias
                    const resAlias = await fetch(ENDPOINTS.POLICIES.BY_SLUG('kcb_offers'));
                    const dataAlias = await resAlias.json();
                    if (dataAlias.isOk && dataAlias.data) {
                        setPolicy(dataAlias.data);
                    }
                }
            } catch (err) {
                console.error("Error fetching KCB policy:", err);
            } finally {
                setLoading(false);
            }
        };
        fetchPolicy();
    }, []);

    return (
        <div className="min-h-screen bg-background pb-4">
            {/* Hero Section */}
            <div className="bg-gradient-to-br from-green-700 via-green-600 to-emerald-800 text-white py-16 lg:py-20 shadow-lg relative overflow-hidden">
                <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,rgba(255,255,255,0.15),transparent_50%)]"></div>
                <div className="max-w-[1400px] mx-auto px-4 text-center relative z-10 max-w-3xl">
                    <div className="w-20 h-20 flex items-center justify-center bg-white/20 backdrop-blur-md rounded-full mx-auto mb-6 shadow-lg border border-white/30">
                        <i className="ri-bank-card-line text-5xl"></i>
                    </div>
                    <h1 className="text-4xl md:text-5xl font-bold mb-4 font-display tracking-tight">
                        {policy?.title || "KCB Bank Offers"}
                    </h1>
                    <p className="text-xl text-white/90 mb-8 max-w-2xl mx-auto font-light">
                        Get your dream Smartphone, Laptop, or Accessory today with flexible payment plans at <strong className="font-bold text-white">0% INTEREST</strong>
                    </p>
                    <div className="flex flex-wrap items-center justify-center gap-3 text-sm font-medium">
                        {['0% Interest Rate', 'Up to 6 Months EMI', 'Quick 24-48h Approval', 'No Hidden Charges'].map(b => (
                            <div key={b} className="flex items-center gap-2 bg-white/15 backdrop-blur-md px-5 py-2.5 rounded-full border border-white/25 shadow-sm">
                                <i className="ri-check-line text-lg text-emerald-300"></i>
                                <span>{b}</span>
                            </div>
                        ))}
                    </div>
                </div>
            </div>

            {/* Dynamic Content from Policy Master or Fallback */}
            {policy?.description ? (
                <div className="py-12 max-w-[1400px] mx-auto px-4">
                    <style>{`
                        .kcb-policy-content {
                            line-height: 1.8;
                            color: #2D3436;
                        }
                        .kcb-policy-content img {
                            max-width: 100%;
                            height: auto;
                            border-radius: 12px;
                        }
                        .kcb-policy-content a {
                            color: #137333;
                            text-decoration: none;
                        }
                        .kcb-policy-content a:hover {
                            text-decoration: underline;
                        }
                    `}</style>
                    <div 
                        className="kcb-policy-content leading-relaxed" 
                        dangerouslySetInnerHTML={{ __html: resolveHtmlAssetUrls(policy.description) }} 
                    />
                </div>
            ) : (
                <>
                    {/* KCB Official Banners */}
                    <div className="py-12 bg-muted/20 border-b border-border">
                        <div className="max-w-[1200px] mx-auto px-4">
                            <div className="flex flex-col gap-8 items-center">
                                <div className="w-full bg-card rounded-2xl p-2 md:p-4 shadow-md border border-border hover:shadow-lg transition-shadow">
                                    <img 
                                        src={resolveAssetUrl("uploads/joedit/kcb-banner-02.jpg")} 
                                        alt="KCB Banner 02 - Smartphone, Laptop and Accessories 0% Interest" 
                                        className="w-full h-auto rounded-xl object-contain mx-auto"
                                    />
                                </div>
                                <div className="w-full bg-card rounded-2xl p-2 md:p-4 shadow-md border border-border hover:shadow-lg transition-shadow">
                                    <img 
                                        src={resolveAssetUrl("uploads/joedit/kcb-banner-03.jpg")} 
                                        alt="KCB Banner 03 - Installment Plans" 
                                        className="w-full h-auto rounded-xl object-contain mx-auto"
                                    />
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* How to Apply Section */}
                    <div className="py-12 max-w-[1400px] mx-auto px-4 mb-4">
                        <div className="text-center mb-12">
                            <span className="bg-primary/10 text-primary font-bold text-xs uppercase px-3 py-1 rounded-full tracking-wider mb-3 inline-block">
                                Simple Step-by-Step Guide
                            </span>
                            <h2 className="text-3xl lg:text-4xl font-bold text-foreground font-display" style={{ fontFamily: "'Playfair Display', serif" }}>
                                This is how to Apply
                            </h2>
                            <div className="w-24 h-1 bg-primary mx-auto mt-4 rounded-full"></div>
                        </div>

                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                            {/* Option 1 */}
                            <div className="bg-card rounded-2xl border border-border p-8 shadow-sm hover:shadow-md transition-all flex flex-col justify-between relative overflow-hidden">
                                <div className="absolute top-0 left-0 w-2 h-full bg-green-600"></div>
                                <div>
                                    <div className="flex items-center justify-between mb-6 pb-4 border-b border-border">
                                        <h3 className="text-2xl font-bold text-foreground flex items-center gap-3">
                                            <span className="w-10 h-10 rounded-full bg-green-100 text-green-700 flex items-center justify-center text-lg font-bold">1</span>
                                            Option 1: Online Application
                                        </h3>
                                        <span className="text-xs font-bold uppercase bg-green-100 text-green-800 px-3 py-1 rounded-full">Recommended</span>
                                    </div>

                                    <ol className="space-y-4 text-muted-foreground mb-8 list-decimal list-inside">
                                        <li className="pl-2 leading-relaxed">
                                            <span className="text-foreground font-medium">Download the application form:</span>
                                            <div className="mt-2 ml-4">
                                                <a 
                                                    href={resolveAssetUrl("uploads/joedit/KCB-APPLICATION-FORM.pdf")} 
                                                    target="_blank" 
                                                    rel="noopener noreferrer"
                                                    className="inline-flex items-center gap-2 bg-green-600 hover:bg-green-700 text-white font-medium px-4 py-2.5 rounded-lg text-sm transition-colors shadow-sm"
                                                >
                                                    <i className="ri-download-2-line text-lg"></i>
                                                    CLICK HERE FOR KCB FINANCING APPLICATION FORM
                                                </a>
                                            </div>
                                        </li>
                                        <li className="pl-2 leading-relaxed"><span className="text-foreground font-medium">Fill and sign the form</span> completely with required details.</li>
                                        <li className="pl-2 leading-relaxed">
                                            <span className="text-foreground font-medium">Scan &amp; send it via email to:</span>
                                            <div className="mt-1 ml-4 flex flex-wrap gap-2 text-sm font-medium">
                                                <a href="mailto:fonexpress@kcbgroup.com" className="text-primary hover:underline bg-primary/10 px-2.5 py-1 rounded">
                                                    fonexpress@kcbgroup.com
                                                </a>
                                                <span className="text-muted-foreground">CC:</span>
                                                <a href="mailto:sales@fonexpress.net" className="text-primary hover:underline bg-primary/10 px-2.5 py-1 rounded">
                                                    sales@fonexpress.net
                                                </a>
                                            </div>
                                        </li>
                                        <li className="pl-2 leading-relaxed">KCB will advise on approval status within <strong className="text-foreground">24 - 48 hours</strong>.</li>
                                        <li className="pl-2 leading-relaxed">We will contact you to notify you and confirm the most convenient branch for you to make the 1st installment and collect the device.</li>
                                        <li className="pl-2 leading-relaxed">The remaining amount will be deducted automatically for the period you selected.</li>
                                    </ol>
                                </div>

                                <div className="bg-muted/40 p-4 rounded-xl border border-border/60 text-xs text-muted-foreground flex items-center gap-3">
                                    <i className="ri-information-line text-lg text-primary shrink-0"></i>
                                    <span>Ideal if you prefer applying from home or office without visiting a physical store first.</span>
                                </div>
                            </div>

                            {/* Option 2 */}
                            <div className="bg-card rounded-2xl border border-border p-8 shadow-sm hover:shadow-md transition-all flex flex-col justify-between relative overflow-hidden">
                                <div className="absolute top-0 left-0 w-2 h-full bg-blue-600"></div>
                                <div>
                                    <div className="flex items-center justify-between mb-6 pb-4 border-b border-border">
                                        <h3 className="text-2xl font-bold text-foreground flex items-center gap-3">
                                            <span className="w-10 h-10 rounded-full bg-blue-100 text-blue-700 flex items-center justify-center text-lg font-bold">2</span>
                                            Option 2: In-Store Application
                                        </h3>
                                        <span className="text-xs font-bold uppercase bg-blue-100 text-blue-800 px-3 py-1 rounded-full">In-Person</span>
                                    </div>

                                    <ol className="space-y-4 text-muted-foreground mb-8 list-decimal list-inside">
                                        <li className="pl-2 leading-relaxed"><span className="text-foreground font-medium">Visit any of our outlets</span> nearest to you.</li>
                                        <li className="pl-2 leading-relaxed"><span className="text-foreground font-medium">Collect and fill</span> an application form directly at the store.</li>
                                        <li className="pl-2 leading-relaxed">
                                            <span className="text-foreground font-medium">Scan &amp; send it via email to:</span>
                                            <div className="mt-1 ml-4 flex flex-wrap gap-2 text-sm font-medium">
                                                <a href="mailto:fonexpress@kcbgroup.com" className="text-primary hover:underline bg-primary/10 px-2.5 py-1 rounded">
                                                    fonexpress@kcbgroup.com
                                                </a>
                                                <span className="text-muted-foreground">CC:</span>
                                                <a href="mailto:sales@fonexpress.net" className="text-primary hover:underline bg-primary/10 px-2.5 py-1 rounded">
                                                    sales@fonexpress.net
                                                </a>
                                            </div>
                                        </li>
                                        <li className="pl-2 leading-relaxed">KCB will send an approval via email within <strong className="text-foreground">24 - 48 hours</strong>.</li>
                                        <li className="pl-2 leading-relaxed">Visit the branch to make the 1st installment and collect your phone once approval is granted!</li>
                                    </ol>
                                </div>

                                {/* Credit Card Offer Banner */}
                                <div className="bg-gradient-to-r from-amber-500/15 via-orange-500/15 to-amber-500/15 p-5 rounded-xl border border-amber-500/30 text-sm">
                                    <div className="flex items-start gap-3">
                                        <div className="w-10 h-10 rounded-lg bg-amber-500/20 text-amber-700 flex items-center justify-center shrink-0 mt-0.5">
                                            <i className="ri-vip-crown-line text-xl"></i>
                                        </div>
                                        <div>
                                            <h4 className="font-bold text-foreground text-base mb-1">Get a KCB CREDIT CARD Today!</h4>
                                            <p className="text-muted-foreground text-xs mb-2 leading-relaxed">
                                                Enjoy exclusive offers, faster approvals, and seamless installment payments with a KCB Credit Card.
                                            </p>
                                            <div className="text-xs font-semibold">
                                                To apply, contact KCB via email: <a href="mailto:fonexpress@kcbgroup.com" className="text-primary hover:underline">fonexpress@kcbgroup.com</a>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </>
            )}
        </div>
    );
}
